import React from 'react';
import { FileText, Download, Star, Filter } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface TemplatesContentProps {
  animationDelay?: number;
}

const templates = [
  { name: 'Lead Capture', category: 'Forms', downloads: '2.4k', rating: 4.9, color: '#3B82F6' },
  { name: 'Welcome Email', category: 'Email', downloads: '1.8k', rating: 4.7, color: '#10B981' },
  { name: 'Sales Funnel', category: 'Funnels', downloads: '3.1k', rating: 4.8, color: '#8B5CF6' },
  { name: 'Webinar Page', category: 'Landing', downloads: '1.5k', rating: 4.6, color: '#F59E0B' },
  { name: 'Follow-up SMS', category: 'SMS', downloads: '980', rating: 4.5, color: '#EC4899' },
  { name: 'Booking Form', category: 'Forms', downloads: '2.1k', rating: 4.8, color: '#14B8A6' },
];

const TemplatesContent: React.FC<TemplatesContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex-1 p-1.5 sm:p-2 lg:p-4 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between mb-1.5 sm:mb-2 lg:mb-3"
        style={{ animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both` }}
      >
        <div>
          <h3 
            className="text-[8px] sm:text-[10px] lg:text-sm font-semibold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            Templates
          </h3>
          <p className="text-[6px] sm:text-[8px] lg:text-[10px] hidden sm:block" style={{ color: isDark ? '#888888' : '#666666' }}>
            156 ready to use
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div 
            className="hidden sm:flex items-center gap-1 px-1.5 lg:px-2 py-0.5 sm:py-1 rounded-md text-[6px] sm:text-[8px] lg:text-[10px]"
            style={{ 
              backgroundColor: isDark ? '#2A2A2A' : '#EBEBEB',
              color: isDark ? '#888888' : '#666666'
            }}
          >
            <Filter className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3" />
            Filter
          </div>
        </div>
      </div>

      {/* Template Grid - 2 cols on mobile, 3 on larger */}
      <div 
        className="grid grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-1.5 lg:gap-2"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {templates.map((template, index) => (
          <div
            key={index}
            className="rounded-lg overflow-hidden group cursor-pointer transition-transform hover:scale-[1.02]"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
              animation: `scaleIn 0.3s ease-out ${animationDelay + 250 + (index * 60)}ms both`
            }}
          >
            {/* Template Preview */}
            <div 
              className="h-8 sm:h-10 lg:h-16 relative overflow-hidden"
              style={{ backgroundColor: `${template.color}10` }}
            >
              <div 
                className="absolute inset-1 sm:inset-2 rounded"
                style={{ 
                  backgroundColor: `${template.color}20`,
                  border: `1px dashed ${template.color}40`
                }}
              >
                <div className="p-1 sm:p-1.5 space-y-0.5 sm:space-y-1">
                  <div 
                    className="h-1 sm:h-1.5 w-3/4 rounded-sm"
                    style={{ backgroundColor: `${template.color}40` }}
                  />
                  <div 
                    className="h-0.5 sm:h-1 w-1/2 rounded-sm"
                    style={{ backgroundColor: `${template.color}30` }}
                  />
                </div>
              </div>
              <FileText 
                className="absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 w-3 h-3 sm:w-4 sm:h-4"
                style={{ color: template.color, opacity: 0.5 }}
              />
            </div>
            
            {/* Template Info */}
            <div className="p-0.5 sm:p-1 lg:p-2">
              <div 
                className="text-[6px] sm:text-[8px] lg:text-[10px] font-medium mb-0.5"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                {template.name}
              </div>
              <div className="flex items-center justify-between">
                <span 
                  className="text-[5px] sm:text-[6px] lg:text-[8px] px-0.5 lg:px-1 py-0.5 rounded"
                  style={{ 
                    backgroundColor: `${template.color}20`,
                    color: template.color
                  }}
                >
                  {template.category}
                </span>
                <div className="flex items-center gap-0.5">
                  <Star className="w-1.5 h-1.5 sm:w-2 sm:h-2 lg:w-2.5 lg:h-2.5 fill-amber-400 text-amber-400" />
                  <span className="text-[5px] sm:text-[6px] lg:text-[8px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                    {template.rating}
                  </span>
                </div>
              </div>
              <div 
                className="hidden sm:flex items-center gap-0.5 mt-0.5 sm:mt-1 text-[5px] sm:text-[6px] lg:text-[8px]"
                style={{ color: isDark ? '#666666' : '#999999' }}
              >
                <Download className="w-1.5 h-1.5 sm:w-2 sm:h-2 lg:w-2.5 lg:h-2.5" />
                {template.downloads}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplatesContent;
